<script lang="ts">
  import type { LawyerListQuery } from '@shared/types/lawyer';
  import type { FirmAffiliatedLawyer, Lawyer } from '@domain/lawyer/model';
  import type { LastContactBucket, NextActionBucket } from '@shared/types/firm';
  import {
    BAR_ASSOCIATION_DISPLAY_OPTIONS,
    CUSTOMER_STATUS_OPTIONS,
    CUSTOMER_STATUS_VALUES,
    LAST_CONTACT_BUCKET_OPTIONS,
    LEGAL_TRAINING_TERM_FILTER_OPTIONS,
    NEXT_ACTION_BUCKET_OPTIONS,
    CUSTOMER_STATUS_LABELS,
    formatBarAssociationLabel,
    type SimpleOption,
  } from '@shared/constants/choices';
  import {
    ACTION_TYPE_LABELS,
    ACTION_TYPE_ORDER,
    type ActionType,
  } from '@shared/types/sales-action';
  import { formatDate } from '@shared/util/format';
  import FilterChip from '@shared/ui/FilterChip.svelte';
  import {
    disciplinaryContactCautionBadgeLabel,
    hasDisciplinaryContactCaution,
  } from '@domain/lawyer/disciplinary-sales-signal';
  import {
    nextActionAttention,
    nextActionAttentionAriaLabel,
  } from '@domain/firm/next-action-attention';
  import { lawyerBulkSalesSummaryLabel } from '@domain/bulk-sales/labels';

  type SortField = NonNullable<LawyerListQuery['sort_by']>;

  interface Props {
    lawyers: Lawyer[];
    query: LawyerListQuery;
    productOptions: SimpleOption[];
    positionOptions: SimpleOption[];
    /** 担当者フィルタの選択肢 (PR-ACC-3)。 ページ側で app_accounts から取得して渡す。 */
    accountOptions: SimpleOption[];
    /** 空状態から検索・列フィルタをまとめて戻す導線の表示制御。 */
    hasActiveFilters: boolean;
    onChange: (next: LawyerListQuery) => void;
    onSort: (field: SortField) => void;
    /** 検索欄も含めて一覧フィルタを初期状態へ戻す。 */
    onClearFilters: () => void;
  }

  let {
    lawyers,
    query,
    productOptions,
    positionOptions,
    accountOptions,
    hasActiveFilters,
    onChange,
    onSort,
    onClearFilters,
  }: Props = $props();

  // 担当者 id → 表示ラベルの索引 (一覧セル表示用)。
  const accountLabelById = $derived(
    new Map(accountOptions.map((o) => [o.value, o.label])),
  );

  const sortBy = $derived<SortField>(query.sort_by ?? 'updated_at');
  const sortDir = $derived(query.sort_dir ?? 'desc');

  const ACTION_TYPE_OPTIONS: SimpleOption[] = ACTION_TYPE_ORDER.map((t) => ({
    value: t,
    label: ACTION_TYPE_LABELS[t],
  }));

  // ステータス選択肢 = 全9値 (受注/失注を含む)。 既定 (= 未指定 or 全選択) のときは
  // チップを中立表示にし、 部分選択のときだけ選択値を見せる。
  const statusChipValue = $derived<string[]>(
    query.sales_statuses &&
      query.sales_statuses.length < CUSTOMER_STATUS_VALUES.length
      ? query.sales_statuses
      : [],
  );

  function applyPositions(values: string[]) {
    onChange({
      ...query,
      positions: values.length > 0 ? values : undefined,
      page: 1,
    });
  }

  function applyBarAssociations(values: string[]) {
    onChange({
      ...query,
      bar_associations: values.length > 0 ? values : undefined,
      page: 1,
    });
  }

  function applyLegalTrainingTerms(values: string[]) {
    onChange({
      ...query,
      legal_training_terms: values.length > 0 ? values : undefined,
      page: 1,
    });
  }

  function applyCustomerStatuses(values: string[]) {
    // クリア (空)・全選択は「絞り込みなし」(全9値表示) としてフィルタを外す。
    onChange({
      ...query,
      sales_grades: undefined,
      sales_statuses:
        values.length > 0 && values.length < CUSTOMER_STATUS_VALUES.length
          ? (values as NonNullable<LawyerListQuery['sales_statuses']>)
          : undefined,
      page: 1,
    });
  }

  function applyNextActionTypes(values: string[]) {
    onChange({
      ...query,
      next_action_types:
        values.length > 0 ? (values as ActionType[]) : undefined,
      page: 1,
    });
  }

  function applyNextActionBuckets(values: string[]) {
    onChange({
      ...query,
      next_action_buckets:
        values.length > 0 ? (values as NextActionBucket[]) : undefined,
      page: 1,
    });
  }

  function applyLastContactBuckets(values: string[]) {
    onChange({
      ...query,
      last_contact_buckets:
        values.length > 0 ? (values as LastContactBucket[]) : undefined,
      page: 1,
    });
  }

  function applyProductIds(values: string[]) {
    onChange({
      ...query,
      product_ids: values.length > 0 ? values : undefined,
      page: 1,
    });
  }

  function applyAssignees(values: string[]) {
    onChange({
      ...query,
      assignee_account_ids: values.length > 0 ? values : undefined,
      page: 1,
    });
  }

  function assigneeLabel(lawyer: Lawyer): string {
    if (!lawyer.assignee_account_id) return '-';
    return (
      accountLabelById.get(lawyer.assignee_account_id) ??
      lawyer.assignee_account_id
    );
  }

  function indicator(field: SortField): string {
    if (field !== sortBy) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  function sortMark(field: SortField): string {
    if (field !== sortBy) return '↕';
    return sortDir === 'asc' ? '▲' : '▼';
  }

  function display(value: string | null | undefined): string {
    return value && value.length > 0 ? value : '-';
  }

  // 次アクション (catalog No.60) は sales_actions.action_type (次アクションマスタ) を表示する。
  function nextActionLabel(value: string | null | undefined): string {
    if (!value) return '-';
    return ACTION_TYPE_LABELS[value as ActionType] ?? value;
  }

  function productNames(lawyer: Lawyer): string[] {
    return (lawyer.active_products ?? []).map((p) => p.name);
  }

  function statusValue(lawyer: Lawyer): string {
    return lawyer.proposal_summary?.representative_status ?? 'no_contact';
  }

  function statusLabel(status: string): string {
    return CUSTOMER_STATUS_LABELS[status] ?? status;
  }

  function firmDisplay(lawyer: FirmAffiliatedLawyer): string {
    return lawyer.firm_official_name ?? lawyer.firm_id;
  }

  function isBlank(value: string | null | undefined): boolean {
    return !value || value.trim().length === 0;
  }

  // 一括営業可否は backend の派生値 (本人 / 現所属事務所 / 懲戒の OR 合成) を描画する
  // だけで、ここで合成を再実装しない (詳細・通常一覧・所属弁護士一覧の判定一致)。
  function bulkSalesLabel(lawyer: Lawyer): string | null {
    return lawyerBulkSalesSummaryLabel(lawyer.bulk_sales_decision);
  }

  function hasFirmCompetitorCaution(lawyer: Lawyer): boolean {
    return lawyer.firm_is_competitor === true;
  }

  function hasSalesCautionBadge(lawyer: Lawyer): boolean {
    return (
      bulkSalesLabel(lawyer) !== null ||
      hasFirmCompetitorCaution(lawyer) ||
      hasDisciplinaryContactCaution(lawyer)
    );
  }

  function isFirmCompetitorReasonMissing(lawyer: Lawyer): boolean {
    return (
      hasFirmCompetitorCaution(lawyer) && isBlank(lawyer.firm_competitor_reason)
    );
  }
</script>

<div class="table-scroll">
  <table data-testid="lawyers-table">
    <colgroup>
      <col class="col-lawyer-name-width" />
      <col class="col-position-width" />
      <col class="col-training-width" />
      <col class="col-firm-width" />
      <col class="col-bar-width" />
      <col class="col-status-width" />
      <col class="col-action-width" />
      <col class="col-date-width" />
      <col class="col-date-width" />
      <col class="col-products-width" />
      <col class="col-assignee-width" />
    </colgroup>
    <thead>
      <tr>
        <th>
          <button
            type="button"
            class="sort"
            onclick={() => onSort('full_name')}
          >
            氏名{indicator('full_name')}
          </button>
        </th>
        <th>
          <div class="th-cell">
            <FilterChip
              label="役職"
              mode="multi"
              options={positionOptions}
              currentValue={query.positions ?? []}
              onApply={applyPositions}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'position'}
              onclick={() => onSort('position')}
              aria-label="役職で並べ替え"
              title="並べ替え">{sortMark('position')}</button
            >
          </div>
        </th>
        <th>
          <div class="th-cell">
            <FilterChip
              label="修習期"
              mode="multi"
              options={LEGAL_TRAINING_TERM_FILTER_OPTIONS}
              currentValue={query.legal_training_terms ?? []}
              onApply={applyLegalTrainingTerms}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'legal_training_term'}
              onclick={() => onSort('legal_training_term')}
              aria-label="修習期で並べ替え"
              title="並べ替え">{sortMark('legal_training_term')}</button
            >
          </div>
        </th>
        <th>
          <button
            type="button"
            class="sort"
            onclick={() => onSort('firm_official_name')}
          >
            所属{indicator('firm_official_name')}
          </button>
        </th>
        <th>
          <div class="th-cell">
            <FilterChip
              label="弁護士会"
              mode="multi"
              options={BAR_ASSOCIATION_DISPLAY_OPTIONS}
              currentValue={query.bar_associations ?? []}
              onApply={applyBarAssociations}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'bar_association'}
              onclick={() => onSort('bar_association')}
              aria-label="弁護士会で並べ替え"
              title="並べ替え">{sortMark('bar_association')}</button
            >
          </div>
        </th>
        <th>
          <div class="th-cell th-cell-nowrap">
            <FilterChip
              label="ステータス"
              mode="multi"
              options={CUSTOMER_STATUS_OPTIONS}
              currentValue={statusChipValue}
              onApply={applyCustomerStatuses}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'status'}
              onclick={() => onSort('status')}
              aria-label="ステータスで並べ替え"
              title="並べ替え">{sortMark('status')}</button
            >
          </div>
        </th>
        <th>
          <div class="th-cell th-cell-nowrap">
            <FilterChip
              label="次アクション"
              mode="multi"
              options={ACTION_TYPE_OPTIONS}
              currentValue={query.next_action_types ?? []}
              onApply={applyNextActionTypes}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'next_action'}
              onclick={() => onSort('next_action')}
              aria-label="次アクションで並べ替え"
              title="並べ替え">{sortMark('next_action')}</button
            >
          </div>
        </th>
        <th>
          <div class="th-cell th-cell-nowrap">
            <FilterChip
              label="対応期限"
              mode="multi"
              options={NEXT_ACTION_BUCKET_OPTIONS}
              currentValue={query.next_action_buckets ?? []}
              onApply={applyNextActionBuckets}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'next_action_due_at'}
              onclick={() => onSort('next_action_due_at')}
              aria-label="対応期限で並べ替え"
              title="並べ替え">{sortMark('next_action_due_at')}</button
            >
          </div>
        </th>
        <th>
          <div class="th-cell th-cell-nowrap">
            <FilterChip
              label="最終接触"
              mode="multi"
              options={LAST_CONTACT_BUCKET_OPTIONS}
              currentValue={query.last_contact_buckets ?? []}
              onApply={applyLastContactBuckets}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'last_contact_at'}
              onclick={() => onSort('last_contact_at')}
              aria-label="最終接触で並べ替え"
              title="並べ替え">{sortMark('last_contact_at')}</button
            >
          </div>
        </th>
        <th class="th-right">
          <div class="th-cell">
            <FilterChip
              label="利用ソリューション"
              mode="multi"
              options={productOptions}
              currentValue={query.product_ids ?? []}
              onApply={applyProductIds}
              variant="table-header"
            />
            <button
              type="button"
              class="sort-mini"
              class:active={sortBy === 'active_products'}
              onclick={() => onSort('active_products')}
              aria-label="利用ソリューションで並べ替え"
              title="並べ替え">{sortMark('active_products')}</button
            >
          </div>
        </th>
        <th>
          <FilterChip
            label="担当者"
            mode="multi"
            options={accountOptions}
            currentValue={query.assignee_account_ids ?? []}
            onApply={applyAssignees}
            variant="table-header"
          />
        </th>
      </tr>
    </thead>
    <tbody>
      {#if lawyers.length === 0}
        <tr class="empty-row">
          <td colspan="11">
            <div class="empty-state">
              <p class="empty">該当する弁護士がいません</p>
              {#if hasActiveFilters}
                <button
                  type="button"
                  class="clear-filters"
                  onclick={onClearFilters}
                >
                  フィルタをクリア
                </button>
              {/if}
            </div>
          </td>
        </tr>
      {:else}
        {#each lawyers as lawyer (lawyer.id)}
          {@const status = statusValue(lawyer)}
          {@const attn = nextActionAttention(lawyer)}
          {@const disciplinaryCautionLabel = disciplinaryContactCautionBadgeLabel(lawyer)}
          {@const bulkSales = bulkSalesLabel(lawyer)}
          <tr
            class:exclusion-row={bulkSales !== null}
            data-lawyer-id={lawyer.id}
          >
            <td class="lawyer-name-cell">
              <div class="lawyer-name-content">
                <a href={`/lawyers/${lawyer.id}`} class="primary-link">
                  {lawyer.full_name}
                </a>
                {#if hasSalesCautionBadge(lawyer)}
                  <div
                    class="sales-caution-badges"
                    aria-label="営業対象外・競合・懲戒の注意表示"
                  >
                    {#if bulkSales}
                      <span
                        class="sales-caution-badge sales-caution-badge-excluded"
                        title={bulkSales}
                      >
                        {bulkSales}
                      </span>
                    {/if}
                    {#if hasFirmCompetitorCaution(lawyer)}
                      <span
                        class="sales-caution-badge sales-caution-badge-competitor"
                        title={isFirmCompetitorReasonMissing(lawyer)
                          ? '競合（事務所）: 理由未設定'
                          : '競合（事務所）'}
                      >
                        競合（事務所）
                        {#if isFirmCompetitorReasonMissing(lawyer)}
                          <span class="reason-missing" aria-label="理由未設定"
                            >!</span
                          >
                        {/if}
                      </span>
                    {/if}
                    {#if disciplinaryCautionLabel}
                      <span
                        class="sales-caution-badge"
                        class:sales-caution-badge-disciplinary-review={lawyer.disciplinary_sales_handling ===
                          'needs_review'}
                        class:sales-caution-badge-disciplinary-stop={lawyer.disciplinary_sales_handling ===
                          'stop_contact'}
                      >
                        {disciplinaryCautionLabel}
                      </span>
                    {/if}
                  </div>
                {/if}
              </div>
            </td>
            <td>{display(lawyer.position)}</td>
            <td>{display(lawyer.legal_training_term)}</td>
            <td>
              {#if lawyer.firm_id !== null}
                <a href={`/firms/${lawyer.firm_id}`} class="muted-link"
                  >{firmDisplay(lawyer)}</a
                >
              {:else}
                <!-- company 所属 (インハウス) は企業名をテキスト表示 (リンクなし)。 -->
                <span class="company-name">{lawyer.company_name ?? '-'}</span>
              {/if}
            </td>
            <td>{formatBarAssociationLabel(lawyer.bar_association)}</td>
            <td>{statusLabel(status)}</td>
            <td class="col-next">
              <div class="next-body" title={lawyer.next_action ?? ''}>
                {nextActionLabel(lawyer.next_action)}
              </div>
              {#if attn.kind !== 'none'}
                <div
                  class="attention"
                  role="group"
                  aria-label={nextActionAttentionAriaLabel(lawyer)}
                >
                  {#if attn.kind === 'review'}
                    <span class="attn-badge attn-review" aria-hidden="true"
                      >要確認</span
                    >
                    {#if attn.showAuto}
                      <span class="attn-badge attn-auto" aria-hidden="true"
                        >自動</span
                      >
                    {/if}
                  {:else}
                    <span class="attn-badge attn-update" aria-hidden="true"
                      >更新</span
                    >
                  {/if}
                </div>
              {/if}
            </td>
            <td>{formatDate(lawyer.next_action_due_at)}</td>
            <td>{formatDate(lawyer.last_contact_at)}</td>
            <td class="col-products">
              {#if productNames(lawyer).length === 0}
                <span class="muted">-</span>
              {:else}
                <div class="product-chips">
                  {#each productNames(lawyer) as name, idx (idx)}
                    <span class="product-chip">{name}</span>
                  {/each}
                </div>
              {/if}
            </td>
            <td class="col-assignee">
              {#if lawyer.assignee_account_id}
                {assigneeLabel(lawyer)}
              {:else}
                <span class="muted">-</span>
              {/if}
            </td>
          </tr>
        {/each}
      {/if}
    </tbody>
  </table>
</div>

<style>
  .table-scroll {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
  }

  table {
    width: 100%;
    min-width: 1360px;
    table-layout: fixed;
  }

  .col-lawyer-name-width {
    width: 9%;
  }
  .col-position-width {
    width: 8%;
  }
  .col-training-width {
    width: 8%;
  }
  .col-firm-width {
    width: 10%;
  }
  .col-bar-width {
    width: 10%;
  }
  .col-status-width {
    width: 10%;
  }
  .col-action-width {
    width: 10%;
  }
  .col-date-width {
    width: 9%;
  }
  .col-products-width {
    width: 10%;
  }
  .col-assignee-width {
    width: 8%;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    background: #f9fafb;
    box-shadow: 0 1px 0 var(--border, #e3e6eb);
  }

  th,
  td {
    text-align: center;
    vertical-align: middle;
    padding: 8px 5px;
    overflow-wrap: anywhere;
  }
  tbody td:nth-child(1),
  tbody td:nth-child(4) {
    text-align: left;
  }

  tbody tr.exclusion-row {
    background: #fff8f8;
    box-shadow: inset 3px 0 0 #ef4444;
  }
  tbody tr:hover {
    background: #f8fafc;
  }
  tbody tr.exclusion-row:hover {
    background: #fff3f3;
  }

  .th-right :global(.popover) {
    left: auto;
    right: 0;
  }
  th:last-child :global(.popover) {
    left: auto;
    right: 0;
  }

  th button.sort {
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
    color: inherit;
    font: inherit;
    font-weight: 600;
  }
  th button.sort:hover {
    color: var(--primary, #2563eb);
  }

  .th-cell {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    max-width: 100%;
  }
  .th-cell-nowrap {
    white-space: nowrap;
  }
  .th-cell :global(.filter-chip) {
    min-width: 0;
    max-width: calc(100% - 16px);
  }
  .th-cell :global(.chip-btn),
  th > :global(.filter-chip .chip-btn) {
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .th-cell-nowrap :global(.chip-btn) {
    white-space: nowrap;
  }

  .sort-mini {
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
    color: var(--muted, #6b7280);
    font-size: 11px;
    line-height: 1;
  }
  .sort-mini:hover,
  .sort-mini.active {
    color: var(--primary, #2563eb);
  }

  .primary-link {
    color: var(--primary, #2563eb);
    font-weight: 500;
    text-decoration: none;
  }
  .primary-link:hover {
    text-decoration: underline;
  }

  .lawyer-name-cell {
    padding-top: 7px;
    padding-bottom: 7px;
  }
  .lawyer-name-content {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    width: 100%;
    max-width: 100%;
  }
  .sales-caution-badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 4px;
  }
  .sales-caution-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 5px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.35;
    white-space: nowrap;
  }
  .sales-caution-badge-excluded {
    background: #fee2e2;
    color: #991b1b;
  }
  .sales-caution-badge-competitor {
    background: #fff7ed;
    color: #9a3412;
  }
  .sales-caution-badge-disciplinary-review {
    background: #fef3c7;
    color: #92400e;
    box-shadow: inset 0 0 0 1px #fbbf24;
  }
  .sales-caution-badge-disciplinary-stop {
    background: #fff1f2;
    color: #9f1239;
    box-shadow: inset 0 0 0 1px #fda4af;
  }
  .reason-missing {
    display: inline-grid;
    place-items: center;
    width: 13px;
    height: 13px;
    border-radius: 999px;
    background: rgba(185, 28, 28, 0.14);
    color: #991b1b;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
  }

  .muted-link {
    color: var(--muted, #6b7280);
    font-size: 12px;
    text-decoration: none;
  }
  .muted-link:hover {
    text-decoration: underline;
  }

  .company-name {
    color: var(--muted, #6b7280);
    font-size: 12px;
  }

  .muted {
    color: var(--muted, #6b7280);
  }

  .product-chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
    max-width: 100%;
  }
  .product-chip {
    display: inline-block;
    max-width: 100%;
    padding: 2px 8px;
    background: #eff6ff;
    color: #1d4ed8;
    border-radius: 999px;
    font-size: 12px;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .col-assignee {
    font-size: 12px;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .col-next .next-body {
    display: inline-block;
    font-size: 13px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }

  /* 営業アクション由来の注意表示 (86eya0dva PR3 で新設)。 FirmTable と同形。 */
  .col-next .attention {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
    margin-top: 3px;
  }
  .attn-badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.35;
    white-space: nowrap;
  }
  .attn-review {
    background: #fef3c7;
    color: #92400e;
  }
  .attn-auto {
    background: #eef2ff;
    color: #3730a3;
  }
  .attn-update {
    background: #e0f2fe;
    color: #075985;
  }

  .empty-row td {
    padding: 0;
  }
  .empty-state {
    display: flex;
    min-height: 128px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 28px;
  }
  .empty {
    margin: 0;
    text-align: center;
    color: var(--muted, #6b7280);
    font-size: 14px;
  }
  .clear-filters {
    border: 1px solid #d1d5db;
    border-radius: 999px;
    background: #ffffff;
    color: #374151;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    padding: 6px 12px;
  }
  .clear-filters:hover {
    border-color: var(--primary, #2563eb);
    color: var(--primary, #2563eb);
    background: #eff6ff;
  }
</style>
